<template>
  <ValidationObserver class="d-flex flex-column h-100" v-slot="validation" ref="observer" tag="div">
    <b-form @submit.prevent="validation.handleSubmit(onSubmit)" v-if="refreshForm" class="d-flex h-100">
      <SidebarLayout :title="$t('Templates')">
        <template v-slot:buttons>
          <add-new-button @click="onTemplateAddClick">
            <font-awesome-icon icon="plus-circle" class="icon"/>
            {{ $t('Add Template') }}
          </add-new-button>
        </template>
        <template v-slot:sidebar>
          <Spinner :show="templates.loading" class="mt-5"/>
          <ul v-if="!templates.loading" class="nav flex-column">
            <li v-for="(template) in templates.rows" :key="`template-li-${template.uuid}`" class="nav-item"
                @click="changeTemplate(template)" :class="{ 'active': template.uuid === selectedTemplate.uuid }">
              <a class="nav-link">
                {{ template.template_type.display_name }}
              </a>
            </li>
          </ul>
          <no-data-available v-if="templates.rows && templates.rows.length === 0" height="5"/>
        </template>
        <template v-slot:content>
          <div class="bg-white templates-wrapper">
            <template v-if="templates.loading">
              <Spinner :show="templates.loading" class="mt-5"/>
            </template>
            <template v-else>
              <div v-if="selectedTemplate">
                <div class="pl-2 pr-2 mt-2">
                  <h5>{{ selectedTemplate.template_type.display_name }}</h5>
                  <p>{{ selectedTemplate.template_type.description }}</p>
                </div>
                <b-tabs v-model="currentTab" class="custom-tabs" content-class="bg-white p-2">
                  <email-form :model="model" @onCkeditorReady="onCkeditorReady"/>
                  <sms-form :model="model"/>
                </b-tabs>
              </div>
              <no-data-available v-if="!selectedTemplate" height="10"/>
            </template>
          </div>
        </template>
        <template v-slot:footer-buttons>
          <delete-button :disabled="!selectedTemplate" class="mr-2" @click="onTemplateRemoveClick"/>
          <submit-button :disabled="!selectedTemplate"/>
        </template>
      </SidebarLayout>
    </b-form>
    <template-modal/>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "../../../core/components/common/Spinner";
  import SubmitButton from "../../../core/components/common/SubmitButton";
  import AddNewButton from "../../../core/components/common/AddNewButton";
  import _ from 'lodash';
  import SidebarLayout from "../../../core/components/layout/DefaultLayout/SidebarLayout";
  import {Utils} from "@/core/services/utils.service";
  import TemplateModel from "@/modules/setup/templates/Template.model";
  import EmailForm from "@/modules/setup/templates/EmailForm";
  import SmsForm from "@/modules/setup/templates/SmsForm";
  import DeleteButton from "@/core/components/common/DeleteButton";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import TemplateModal from "@/modules/setup/templates/TemplateModal";

  const {mapState, mapActions} = createNamespacedHelpers('setup/templates');

  export default {
    name: "Template",
    components: {
      TemplateModal,
      NoDataAvailable,
      DeleteButton,
      SmsForm,
      EmailForm,
      SidebarLayout, AddNewButton, SubmitButton, Spinner
    },
    data() {
      return {
        model: new TemplateModel(),
        oldState: null,
        isCkeditorReady: false,
        currentTab: 0,
        refreshForm: true
      }
    },
    computed: {
      ...mapState(['templates', 'selectedTemplate']),
    },
    watch: {
      selectedTemplate() {
        this.initModel();
      }
    },
    methods: {
      ...mapActions(['getTemplates', 'selectTemplate', 'updateTemplate', 'deleteTemplate', 'showModal']),
      onTemplateRemoveClick() {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following template?"),
          this.$i18n.t('Template was successfully deleted'),
          this.deleteTemplate,
          this.selectedTemplate
        );
      },
      onTemplateAddClick() {
        this.showModal()
      },
      async onSubmit() {
        this.model.resetErrors();
        const res = await this.updateTemplate(this.model);
        if (res.success) {
          this.refreshForm = false
          this.$toast('success', this.$t('Template was successfully updated'))
          this.$nextTick(() => {
            this.refreshForm = true
          })
        } else {
          if (res.body.non_feild_errors) {
            this.$errorDialog(res.body.non_field_errors[0])
          } else {
            this.model.setMultipleErrors(res.body);
          }
        }
      },
      changeTemplate(template) {
        this.selectTemplate(_.cloneDeep(template));

        // if (this.oldState && this.dataChanged()) {
        //   this.$confirm(this.$i18n.t('Are you sure you want discard current changes?')).then(async (success) => {
        //     if (success) this.selectTemplate(_.cloneDeep(template))
        //   });
        // } else {
        //   this.selectTemplate(_.cloneDeep(template));
        // }
      },
      getState() {
        if (!this.model) {
          return false;
        }
        const myData = {...this.model};
        delete myData.rules;
        delete myData.errors;
        delete myData.attributeLabels;
        delete myData.attributePlaceholders;
        return JSON.stringify(myData);
      },
      dataChanged() {
        return Utils.isDifferent(JSON.parse(this.getState()), JSON.parse(this.oldState));
      },
      initModel() {
        if (this.selectedTemplate && this.selectedTemplate.uuid && this.isCkeditorReady) {
          this.model = new TemplateModel(_.cloneDeep(this.selectedTemplate));
          this.oldState = this.getState();
        } else {
          this.oldState = this.getState();
          this.model = new TemplateModel();
        }
      },
      onCkeditorReady() {
        this.isCkeditorReady = true;
        this.initModel();
      }
    },
    async beforeMount() {
      await this.getTemplates()
      this.oldState = this.getState();

      if (this.$route.params.template_uuid) {
        this.changeTemplate(this.templates.rows.find(dep => dep.uuid === this.$route.params.template_uuid))
      } else if (this.templates.rows.length > 0) {
        this.changeTemplate(this.templates.rows[0]);
      }
    },
    async beforeRouteLeave(to, from, next) {
      next();
      // if (this.dataChanged()) {
      //   let success = await this.$confirm(this.$i18n.t('Are you sure you want discard current changes?'));
      //   if (success) next();
      // } else {
      //   next();
      // }
    },
  }
</script>

<style lang="scss" scoped>
  @import "./../../../scss/index";

  /deep/ .ck-editor__editable_inline {
    height: 350px !important;
  }

  .templates-wrapper {
    height: calc(100vh - 17em);
    overflow-y: auto;
  }

  /deep/ .custom-tabs .nav.nav-tabs {
    border-top: none;
  }
</style>
